import { useToggle } from '@hooks/useToggle';
import { Button, FormGroup, Input, Label } from 'reactstrap';
import { SurveyUserRoles } from '@constants/user';
import { roles } from '@constants/roles';
import { hasUserRolePermission } from './utils';
import { DelegatedUsersTableProps } from './types';
import { Table } from '@g17eco/molecules/table';
import { UserRow, WorkgroupRow } from './UserRow';
import { filterUsersByRole, putCurrentUserToTop, UTRV_DELEGATE_ROLES } from '@utils/delegation-users';
import { UtrvPermissions } from '@services/permissions/UtrvPermissions';
import { isDelegatedWorkgroup } from '@api/survey-users';
import { ASSIGNMENT } from '@constants/terminology';

export const DelegatedUsersTable = (props: DelegatedUsersTableProps) => {
  const { canAccessVerification, isReadonly } = props;
  const [showExplicitDelegateOnly, toggleShowExplicitDelegateOnly] = useToggle(true);
  return (
    <>
      <ShowAllDelegatedUsersSwitch shown={showExplicitDelegateOnly} toggle={toggleShowExplicitDelegateOnly} />
      <div className='row user-lists mt-3'>
        {canAccessVerification ? (
          UTRV_DELEGATE_ROLES.map((userRole) => (
            <UsersTable key={userRole} {...{ ...props, userRole, showExplicitDelegateOnly }} />
          ))
        ) : (
          <UsersTable {...{ ...props, userRole: SurveyUserRoles.Stakeholder, showExplicitDelegateOnly }} />
        )}
      </div>
      {isReadonly ? (
        <p className='text-center mb-0 theme-TextMedium'>
          This survey has been completed and is locked. Delegation is not possible.
        </p>
      ) : null}
    </>
  );
};

const ShowAllDelegatedUsersSwitch = ({ shown, toggle }: { shown: boolean; toggle: () => void }) => {
  return (
    <FormGroup switch className='d-flex align-items-center justify-content-end pr-1'>
      <Input type='switch' checked={!shown} id='view-all-users' className='m-0' onChange={toggle} />
      <Label className='ml-1' check>
        Include {ASSIGNMENT.roleAccess.toLowerCase()} metrics
      </Label>
    </FormGroup>
  );
};

type UsersTableProps = DelegatedUsersTableProps & {
  userRole: SurveyUserRoles.Stakeholder | SurveyUserRoles.Verifier;
  showExplicitDelegateOnly: boolean;
};

const UsersTable = (props: UsersTableProps) => {
  const { canAccessVerification, userRole, startDelegating, currentUser, survey, utrvs, initiativeId, isReadonly } =
    props;
  const className = !canAccessVerification ? 'col' : 'col-12 col-lg-6 pb-2 pb-lg-0 pr-lg-0';
  const canManageUser = hasUserRolePermission(userRole, utrvs, survey, currentUser, initiativeId) && !isReadonly;

  return (
    <div className={className}>
      <UserList {...props} isReadonly={!canManageUser} />
      {canManageUser ? <AddBtn userRole={userRole} handleAdd={() => startDelegating(userRole)} /> : null}
    </div>
  );
};

const AddBtn = ({ userRole, handleAdd }: { handleAdd: () => void; userRole: SurveyUserRoles }) => {
  const title = `Add a ${roles[userRole].shortName}`;

  return (
    <div className='p-1 text-right'>
      <Button color='secondary' onClick={handleAdd}>
        {title}
      </Button>
    </div>
  );
};

const UserList = (props: UsersTableProps) => {
  const {
    showExplicitDelegateOnly,
    userRole,
    users: rawUsers,
    workgroups,
    currentUser,
    utrvs,
    survey,
    handleRemoveUser,
    isReadonly,
  } = props;

  const filteredUsers = filterUsersByRole(rawUsers, userRole);

  const users = showExplicitDelegateOnly
    ? filteredUsers.filter((user) =>
        UtrvPermissions.canUtrvExplicitBulk({
          utrvs,
          survey,
          user,
          role: userRole,
          iteratorFn: (cb) => utrvs.some(cb),
        }),
      )
    : filteredUsers;

  const sortedUsers = putCurrentUserToTop(userRole, users, currentUser);

  const title = `${roles[userRole].shortName}s`;

  const filteredWorkgroups = workgroups.filter((workgroup) => workgroup.permission.roles.includes(userRole));

  const rows = [...sortedUsers, ...filteredWorkgroups];

  return (
    <div className='user-list m-1'>
      <Table
        data={rows}
        columns={[
          {
            id: 'name',
            header: () => (
              <div className='d-flex justify-content-between align-items-center'>
                <span className='text-ThemeTextDark text-uppercase mr-2'>{title}</span>
                <span className='fw-normal'>
                  {sortedUsers.length} / {filteredUsers.length}
                </span>
              </div>
            ),
            cell: ({ row }) => {
              if (isDelegatedWorkgroup(row.original)) {
                return (
                  <WorkgroupRow
                    workgroup={row.original}
                    utrvs={utrvs}
                    handleRemoveWorkgroup={handleRemoveUser}
                    userRole={userRole}
                    isReadonly={isReadonly}
                  />
                );
              }
              return (
                <UserRow
                  userRole={userRole}
                  user={row.original}
                  utrvs={utrvs}
                  handleRemoveUser={handleRemoveUser}
                  isReadonly={isReadonly}
                  survey={survey}
                />
              );
            },
            enableSorting: false,
          },
        ]}
      />
    </div>
  );
};

